import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { collection, doc } from "firebase/firestore";
import { toast } from "sonner";
import { ArrowLeft, Save } from "lucide-react";
import { db } from "@/lib/firebase";
import { getOne, createItem, updateItem } from "@/lib/adminRepository";
import RichTextEditor from "@/components/admin/RichTextEditor";
import ImageUploader from "@/components/admin/ImageUploader";
import FloorplanUploader from "@/components/admin/FloorplanUploader";
import { LISTING_CATEGORIES, COMMERCIAL_CATEGORY, COMMERCIAL_TYPES } from "@/lib/listingCategories";
import { ADMIN } from "@/constants/testIds";

const LISTING_STATUSES = ["Available", "Coming Soon", "Sold", "Rented"];

const BLANK = {
  slug: "", title: "", category: LISTING_CATEGORIES[0], commercialType: "", allowableTrades: "",
  type: "", location: "", district: "",
  description: "", price: "", priceValue: "", bedrooms: "", bathrooms: "",
  area: "", areaValue: "", tenure: "", image: "", gallery: [], floorplan: "",
  externalLink: "", videoLink: "",
  waMessage: "", listingStatus: "Available", order: 99, status: "draft",
};

const slugify = (s) => s.toLowerCase().trim().replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "");

// Small themed field wrappers.
const Label = ({ children }) => <span className="editorial-label">{children}</span>;
const Hint = ({ children }) => <span className="block text-[11px] text-olive/80 mb-1">{children}</span>;

export default function ListingForm() {
  const { slug: routeSlug } = useParams();
  const isNew = !routeSlug || routeSlug === "new";
  const navigate = useNavigate();

  const [form, setForm] = useState(BLANK);
  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);

  // Stable Storage folder so uploads work before the first save (MeProperty pattern).
  const uploadId = useMemo(
    () => (isNew ? doc(collection(db, "listings")).id : routeSlug),
    [isNew, routeSlug]
  );
  const storagePath = `media/listings/${uploadId}`;

  useEffect(() => {
    if (isNew) return;
    let cancelled = false;
    (async () => {
      try {
        const data = await getOne("listings", routeSlug);
        if (cancelled) return;
        if (data) {
          const next = { ...BLANK, ...data };
          // Back-fill numeric values from legacy display strings ("1,184 sqft" / "S$5,000,000").
          if ((next.areaValue === "" || next.areaValue == null) && next.area) {
            const digits = String(next.area).replace(/[^\d]/g, "");
            if (digits) next.areaValue = Number(digits);
          }
          if ((next.priceValue === "" || next.priceValue == null) && next.price) {
            const digits = String(next.price).replace(/[^\d]/g, "");
            if (digits) next.priceValue = Number(digits);
          }
          setForm(next);
        } else toast.error("Listing not found.");
      } catch (err) {
        // eslint-disable-next-line no-console
        console.error("[listing] load failed:", err);
        toast.error("Failed to load listing.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [isNew, routeSlug]);

  const set = (k) => (v) =>
    setForm((f) => ({ ...f, [k]: v && v.target ? v.target.value : v }));

  const num = (v) => (v === "" || v == null ? "" : Number(v) || 0);

  // Single area input: type the sqft number once; the display string
  // ("1,184 sqft") is derived automatically so we don't ask for it twice.
  const setArea = (e) => {
    const raw = e.target.value;
    const n = raw === "" ? "" : Number(raw) || 0;
    setForm((f) => ({
      ...f,
      areaValue: n,
      area: n ? `${n.toLocaleString("en-US")} sqft` : "",
    }));
  };

  // Single price input: type the number once; the display ("S$5,000,000") is
  // derived automatically.
  const setPrice = (e) => {
    const raw = e.target.value;
    const n = raw === "" ? "" : Number(raw) || 0;
    setForm((f) => ({
      ...f,
      priceValue: n,
      price: n ? `S$${n.toLocaleString("en-US")}` : "",
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const slug = isNew ? (form.slug.trim() || slugify(form.title)) : routeSlug;
    if (!form.title.trim()) return toast.error("Title is required.");
    if (!slug) return toast.error("Slug is required (or enter a title to auto-generate one).");

    setSaving(true);
    try {
      const { id, ...rest } = form;
      const isCommercial = form.category === COMMERCIAL_CATEGORY;
      const payload = {
        ...rest,
        slug,
        priceValue: num(form.priceValue),
        areaValue: num(form.areaValue),
        bedrooms: num(form.bedrooms),
        bathrooms: num(form.bathrooms),
        order: num(form.order) || 99,
        // Commercial-only fields are cleared when another category is chosen.
        commercialType: isCommercial ? form.commercialType : "",
        allowableTrades: isCommercial ? form.allowableTrades : "",
      };
      if (isNew) {
        await createItem("listings", payload, slug);
        toast.success("Listing created.");
      } else {
        await updateItem("listings", slug, payload);
        toast.success("Listing saved.");
      }
      navigate("/admin/listings");
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error("[listing] save failed:", err);
      toast.error("Save failed: " + (err?.message || "error"));
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <p className="text-olive">Loading…</p>;

  return (
    <div data-testid="admin-listing-form">
      <Link to="/admin/listings" className="inline-flex items-center gap-2 text-[0.7rem] tracking-[0.22em] uppercase text-bronze hover:text-bronze_dark mb-5">
        <ArrowLeft size={12} /> Back to listings
      </Link>
      <header className="mb-7">
        <div className="eyebrow text-bronze mb-1">{isNew ? "New" : "Edit"}</div>
        <h1 className="font-serif text-3xl md:text-4xl text-ink">{isNew ? "New Listing" : form.title || "Edit listing"}</h1>
      </header>

      <form onSubmit={handleSubmit} className="grid lg:grid-cols-[1fr,300px] gap-6">
        {/* Main column */}
        <div className="space-y-6">
          <section className="border border-bronze/20 bg-cream p-6 space-y-5">
            <label className="block">
              <Label>Title *</Label>
              <input className="editorial-input" value={form.title} onChange={set("title")} data-testid="listing-title" placeholder="e.g. Sky-High 3-Bedder at Marina" />
            </label>

            <label className="block">
              <Label>Slug (URL)</Label>
              {isNew ? (
                <>
                  <Hint>Leave blank to auto-generate from the title. Used in /listings/&lt;slug&gt;.</Hint>
                  <input className="editorial-input" value={form.slug} onChange={set("slug")} data-testid="listing-slug" placeholder="sky-high-3-bedder" />
                </>
              ) : (
                <input className="editorial-input opacity-70" value={routeSlug} readOnly data-testid="listing-slug" />
              )}
            </label>

            <label className="block">
              <Label>Category</Label>
              <Hint>Used to filter listings on the public Listings page.</Hint>
              <select className="editorial-input" value={form.category} onChange={set("category")} data-testid="listing-category">
                {LISTING_CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
            </label>

            {form.category === COMMERCIAL_CATEGORY && (
              <div className="grid sm:grid-cols-2 gap-5 border-l-2 border-bronze/30 pl-4">
                <label className="block">
                  <Label>Type</Label>
                  <Hint>Commercial / industrial sub-type.</Hint>
                  <select className="editorial-input" value={form.commercialType} onChange={set("commercialType")} data-testid="listing-commercial-type">
                    <option value="">Select type…</option>
                    {COMMERCIAL_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
                  </select>
                </label>
                <label className="block">
                  <Label>Allowable trades</Label>
                  <Hint>Free text — e.g. F&amp;B, light industrial, warehouse.</Hint>
                  <input className="editorial-input" value={form.allowableTrades} onChange={set("allowableTrades")} data-testid="listing-allowable-trades" placeholder="e.g. F&B, retail, light industrial" />
                </label>
              </div>
            )}

            <div className="grid sm:grid-cols-2 gap-5">
              <label className="block">
                <Label>Property type</Label>
                <Hint>Free-text descriptor shown on the card/detail (e.g. “Condominium”).</Hint>
                <input className="editorial-input" value={form.type} onChange={set("type")} data-testid="listing-type" placeholder="Condominium" />
              </label>
              <label className="block">
                <Label>Tenure</Label>
                <input className="editorial-input" value={form.tenure} onChange={set("tenure")} data-testid="listing-tenure" placeholder="99-year leasehold" />
              </label>
              <label className="block">
                <Label>Location</Label>
                <input className="editorial-input" value={form.location} onChange={set("location")} data-testid="listing-location" placeholder="Marina Bay" />
              </label>
              <label className="block">
                <Label>District</Label>
                <input className="editorial-input" value={form.district} onChange={set("district")} data-testid="listing-district" placeholder="D1" />
              </label>
            </div>

            <div className="block">
              <Label>Description</Label>
              <Hint>Use the toolbar to format — bold, headings, lists, links, images. Shown on the listing detail page.</Hint>
              <RichTextEditor value={form.description} onChange={set("description")} placeholder="Describe the property…" />
            </div>
          </section>

          <section className="border border-bronze/20 bg-cream p-6 space-y-5">
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-5">
              <label className="block">
                <Label>Price (S$)</Label>
                <Hint>{form.price ? `Shows as “${form.price}”` : "Just the number — “S$” is added for you."}</Hint>
                <input type="number" min="0" className="editorial-input" value={form.priceValue} onChange={setPrice} data-testid="listing-price-value" placeholder="2500000" />
              </label>
              <label className="block">
                <Label>Area (sqft)</Label>
                <Hint>{form.area ? `Shows as “${form.area}”` : "Just the number — “ sqft” is added for you."}</Hint>
                <input type="number" min="0" className="editorial-input" value={form.areaValue} onChange={setArea} data-testid="listing-area-value" placeholder="1184" />
              </label>
              <label className="block">
                <Label>Bedrooms</Label>
                <input type="number" min="0" className="editorial-input" value={form.bedrooms} onChange={set("bedrooms")} data-testid="listing-bedrooms" />
              </label>
              <label className="block">
                <Label>Bathrooms</Label>
                <input type="number" min="0" className="editorial-input" value={form.bathrooms} onChange={set("bathrooms")} data-testid="listing-bathrooms" />
              </label>
            </div>
            <div className="grid sm:grid-cols-2 gap-5">
              <label className="block">
                <Label>External listing link</Label>
                <Hint>Optional — PropertyGuru / 99.co / SRX. Shows a “View on Portal” button. Opens in a new tab.</Hint>
                <input type="url" className="editorial-input" value={form.externalLink} onChange={set("externalLink")} data-testid="listing-external-link" placeholder="https://www.propertyguru.com.sg/listing/…" />
              </label>
              <label className="block">
                <Label>External video link</Label>
                <Hint>Optional — YouTube / Vimeo home tour. Shows a “View Home Tour” button.</Hint>
                <input type="url" className="editorial-input" value={form.videoLink} onChange={set("videoLink")} data-testid="listing-video-link" placeholder="https://www.youtube.com/watch?v=…" />
              </label>
            </div>
            <label className="block">
              <Label>WhatsApp prefill message</Label>
              <textarea rows={2} className="editorial-input resize-y" value={form.waMessage} onChange={set("waMessage")} data-testid="listing-wa" placeholder="Hi Kiap, I'm interested in this listing." />
            </label>
          </section>

          <section className="border border-bronze/20 bg-cream p-6">
            <div className="eyebrow text-bronze mb-1">Media</div>
            <h2 className="font-serif text-lg text-ink mb-4">Cover image</h2>
            <ImageUploader path={storagePath} value={form.image ? [form.image] : []} onChange={(urls) => set("image")(urls[0] || "")} single testid="listing-cover" />
            <h2 className="font-serif text-lg text-ink mt-6 mb-3">Gallery</h2>
            <ImageUploader path={storagePath} value={form.gallery} onChange={set("gallery")} max={12} testid="listing-gallery" />
            <p className="text-[11px] text-olive mt-3">The cover image leads the detail gallery; drag gallery images with the arrows, or star one to make it primary.</p>
            <h2 className="font-serif text-lg text-ink mt-6 mb-3">Floorplan</h2>
            <FloorplanUploader path={storagePath} value={form.floorplan} onChange={set("floorplan")} testid="listing-floorplan" />
            <p className="text-[11px] text-olive mt-2">PDF or image — shown on the listing page with click-to-expand.</p>
          </section>
        </div>

        {/* Sidebar */}
        <aside className="space-y-6">
          <section className="border border-bronze/20 bg-cream p-6 space-y-5">
            <label className="block">
              <Label>Visibility</Label>
              <select className="editorial-input" value={form.status} onChange={set("status")} data-testid="listing-status">
                <option value="published">published</option>
                <option value="draft">draft</option>
              </select>
            </label>
            <label className="block">
              <Label>Market status</Label>
              <select className="editorial-input" value={form.listingStatus} onChange={set("listingStatus")} data-testid="listing-market-status">
                {LISTING_STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
            </label>
            <label className="block">
              <Label>Sort order</Label>
              <Hint>Lower numbers appear first.</Hint>
              <input type="number" className="editorial-input" value={form.order} onChange={set("order")} data-testid="listing-order" />
            </label>
          </section>

          <div className="flex gap-3">
            <Link to="/admin/listings" className="btn-secondary flex-1 text-center">Cancel</Link>
            <button type="submit" disabled={saving} data-testid={ADMIN.saveButton} className="btn-primary flex-1 justify-center disabled:opacity-60">
              <Save size={14} /> {saving ? "Saving…" : isNew ? "Create" : "Save"}
            </button>
          </div>
        </aside>
      </form>
    </div>
  );
}
